import { computed, effect, inject, Injectable, signal } from '@angular/core';

import { AccountResponseV1 } from '@core/models/account';
import { UserResponseV1 } from '@core/models/user';

import { AccountService } from '../account/account';
import { LogService } from '../log/log';
import { UserService } from '../user/user';

declare const pendo: any;

@Injectable({ providedIn: 'root' })
export class PendoService {
  private readonly userService = inject(UserService);
  private readonly accountService = inject(AccountService);
  private readonly logService = inject(LogService);

  private readonly user = this.userService.user;
  private readonly authAccount = computed(() => this.accountService.accountMap()[this.user()?.userId ?? '']);

  private readonly initialized = signal<boolean>(false);

  constructor() {
    effect(() => {
      const user = this.user();
      const account = this.authAccount();
      const initialized = this.initialized();
      if (user && account && !initialized) {
        this.init(user, account);
      } else if (!user && initialized) {
        this.remove();
      }
    });
  }

  init(user: UserResponseV1, account: AccountResponseV1): void {
    this.initialized.set(true);
    try {
      pendo.initialize({
        visitor: {
          id: user.userId,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName
        },
        account: {
          id: account.accountId
        }
      });
      this.logService.info('[PendoService] initialized');
    } catch (error) {
      this.logService.warn('[PendoService] Pendo SDK not available', error);
    }
  }

  remove(): void {
    this.initialized.set(false);
    try {
      pendo.teardown();
      this.logService.info('[PendoService] removed');
    } catch (error) {
      this.logService.warn('[PendoService] Pendo SDK not available for teardown', error);
    }
  }
}
